import { useEffect, useState, type FormEvent } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { api, ApiError, type InvitePublic, type UserMe } from "../api.ts";
import { useAuth } from "../lib/auth.tsx";

const ROLE_LABEL: Record<string, string> = {
  admin: "administrador",
  member: "membro",
  viewer: "visualizador",
};

export function AcceptInvitePage() {
  const { token } = useParams<{ token: string }>();
  const { refresh } = useAuth();
  const nav = useNavigate();

  const [loading, setLoading] = useState(true);
  const [invite, setInvite] = useState<InvitePublic | null>(null);
  const [name, setName] = useState("");
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    let active = true;
    api
      .get<InvitePublic>(`/api/invite/${token}`)
      .then((data) => active && setInvite(data))
      .catch(() => active && setInvite(null))
      .finally(() => active && setLoading(false));
    return () => {
      active = false;
    };
  }, [token]);

  const onSubmit = async (e: FormEvent) => {
    e.preventDefault();
    setError(null);
    if (password.length < 8) return setError("A senha precisa ter pelo menos 8 caracteres.");
    if (password !== confirm) return setError("As senhas não conferem.");
    setBusy(true);
    try {
      const res = await api.post<UserMe & { existing?: boolean }>(
        `/api/invite/${token}/accept`,
        { name, password },
      );
      if (res.existing) {
        nav("/login", { replace: true, state: { msg: "Você já tem conta — faça login." } });
        return;
      }
      await refresh();
      const slug = res.memberships[0]?.slug;
      nav(slug ? `/t/${slug}/strategic` : "/tenants", { replace: true });
    } catch (err) {
      if (err instanceof ApiError && err.status === 400) setError("Senha inválida (mínimo 8 caracteres).");
      else if (err instanceof ApiError && err.status === 404) setError("Convite inválido ou expirado.");
      else setError("Erro inesperado ao aceitar o convite.");
    } finally {
      setBusy(false);
    }
  };

  if (loading) return <div className="center-loading">carregando…</div>;

  if (!invite) {
    return (
      <div className="auth-shell">
        <div className="auth-card">
          <p className="eyebrow">Convite</p>
          <h1 className="crown">Convite inválido ou expirado</h1>
          <p className="sub">Peça um novo link de convite ao administrador.</p>
          <Link className="btn" to="/login" style={{ textAlign: "center", textDecoration: "none" }}>
            Ir para o login
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="auth-shell">
      <div className="auth-card">
        <p className="eyebrow">Convite</p>
        <h1 className="crown">Você foi convidado</h1>
        <p className="sub">
          Para <strong>{invite.tenant_name}</strong> como{" "}
          <strong>{ROLE_LABEL[invite.role] ?? invite.role}</strong>. Defina sua senha para ativar o acesso.
        </p>
        <form className="form" onSubmit={onSubmit}>
          <label>
            E-mail
            <input type="email" value={invite.email} readOnly disabled />
          </label>
          <label>
            Nome
            <input
              type="text"
              autoComplete="name"
              value={name}
              onChange={(e) => setName(e.target.value)}
              autoFocus
            />
          </label>
          <label>
            Senha
            <input
              type="password"
              required
              autoComplete="new-password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
            />
          </label>
          <label>
            Confirmar senha
            <input
              type="password"
              required
              autoComplete="new-password"
              value={confirm}
              onChange={(e) => setConfirm(e.target.value)}
            />
          </label>
          {error && <div className="err">{error}</div>}
          <button className="btn" type="submit" disabled={busy}>
            {busy ? "Ativando…" : "Ativar acesso"}
          </button>
        </form>
      </div>
    </div>
  );
}
